/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { IWssEvent, WssEventEmitter, WssEventHandler } from "@/lib/wss";
import { IWssSubscriptionConfig, IWssSubscriptionContext, IWssSubscriptions } from "@/lib/wss/wss-subscription.types";
import { Component, Vue, Watch } from "vue-property-decorator";

/**
 * Internal tracking for active subscriptions
 */
interface IActiveSubscription {
  event: string;
  handler: WssEventHandler;
  debounceTimer?: ReturnType<typeof setTimeout>;
}

/**
 * Vue mixin that provides declarative WebSocket event subscription management.
 *
 * Components using this mixin can define a `wssSubscriptions` getter that returns
 * subscription configurations. The mixin handles:
 * - Auto-subscribe on mount
 * - Auto-unsubscribe on destroy
 * - Debouncing of rapid events
 * - Filter-based event handling
 * - Context-aware handlers with access to route params
 *
 * @example
 * ```typescript
 * @Component({})
 * export default class MyComponent extends mixins(WssSubscriptionMixin) {
 *   get wssSubscriptions(): IWssSubscriptions {
 *     return {
 *       subscriptions: [
 *         {
 *           event: WssEventNameEnum.MESSAGE_CREATED,
 *           filter: (event) => event.data.conversationId === this.conversationId,
 *           handler: () => this.refreshMessages(),
 *           debounce: 500
 *         }
 *       ]
 *     };
 *   }
 *
 *   async refreshMessages() {
 *     // Refresh logic
 *   }
 * }
 * ```
 */
@Component({})
export default class WssSubscriptionMixin extends Vue {
  /**
   * Track active subscriptions for cleanup
   */
  private _wssActiveSubscriptions: IActiveSubscription[] = [];

  /**
   * Flag to track if subscriptions are currently active
   */
  private _wssSubscriptionsActive = false;

  /**
   * Override this getter in your component to define subscriptions.
   * Return null or { subscriptions: [] } to have no subscriptions.
   */
  get wssSubscriptions(): IWssSubscriptions | null {
    return null;
  }

  /**
   * Watch for changes to the subscription config
   */
  @Watch("wssSubscriptions", { deep: true })
  private onWssSubscriptionsChanged(): void {
    // Re-subscribe when config changes
    this.$wssUnsubscribe();
    this.$wssSubscribe();
  }

  /**
   * Lifecycle hook - subscribe to events on mount
   */
  mounted(): void {
    this.$log.debug("Mounted");
    this._wssActiveSubscriptions = [];
    this.$wssSubscribe();
  }

  /**
   * Lifecycle hook - unsubscribe from events on destroy
   */
  beforeDestroy(): void {
    this.$log.debug("beforeDestroy");
    this.$wssUnsubscribe();
  }

  /**
   * Manually subscribe to all configured events.
   * Called automatically on mount, but can be called manually if needed.
   */
  $wssSubscribe(): void {
    const config = this.wssSubscriptions;

    if (!config || config.disabled || !config.subscriptions?.length) {
      return;
    }

    if (this._wssSubscriptionsActive) {
      return;
    }

    this._wssSubscriptionsActive = true;

    for (const subscription of config.subscriptions) {
      this._createSubscription(subscription);
    }
  }

  /**
   * Manually unsubscribe from all events.
   * Called automatically on beforeDestroy, but can be called manually if needed.
   */
  $wssUnsubscribe(): void {
    for (const active of this._wssActiveSubscriptions) {
      // Clear any pending debounce timers
      if (active.debounceTimer) {
        clearTimeout(active.debounceTimer);
      }
      WssEventEmitter.off(active.event, active.handler);
    }
    this._wssActiveSubscriptions = [];
    this._wssSubscriptionsActive = false;
  }

  /**
   * Manually subscribe to a single event.
   * Useful for dynamic subscriptions.
   *
   * @param config - The subscription configuration
   * @returns A function to unsubscribe
   */
  $wssSubscribeOne<T = Record<string, unknown>>(config: IWssSubscriptionConfig<T>): () => void {
    return this._createSubscription(config);
  }

  /**
   * Create the subscription context for handlers
   */
  private _createContext(unsubscribe: () => void): IWssSubscriptionContext {
    return {
      component: this,
      routeParams: this.$route?.params || {},
      unsubscribe
    };
  }

  /**
   * Create a single subscription and track it
   */
  private _createSubscription<T = Record<string, unknown>>(config: IWssSubscriptionConfig<T>): () => void {
    const activeSubscription: IActiveSubscription = {
      event: config.event,
      handler: () => {
        /* placeholder, replaced below */
      }
    };

    // Create unsubscribe function
    const unsubscribe = (): void => {
      if (activeSubscription.debounceTimer) {
        clearTimeout(activeSubscription.debounceTimer);
      }
      WssEventEmitter.off(config.event, activeSubscription.handler);
      const index = this._wssActiveSubscriptions.indexOf(activeSubscription);
      if (index > -1) {
        this._wssActiveSubscriptions.splice(index, 1);
      }
    };

    // Create the context
    const context = this._createContext(unsubscribe);

    // Create the wrapped handler
    const componentName = this.$options.name || "UnknownComponent";
    const wrappedHandler: WssEventHandler<T> = (event: IWssEvent<T>) => {
      this.$log.debug(`[WssSubscription][${componentName}] Event received:`, config.event, event);

      // Apply filter if present
      if (config.filter) {
        const filterResult = config.filter(event, context);
        this.$log.debug(`[WssSubscription][${componentName}] Filter result:`, filterResult);
        if (!filterResult) {
          return;
        }
      }

      // Handle debounce
      if (config.debounce && config.debounce > 0) {
        this.$log.debug(`[WssSubscription][${componentName}] Debouncing handler for`, config.debounce, "ms");
        if (activeSubscription.debounceTimer) {
          clearTimeout(activeSubscription.debounceTimer);
        }
        activeSubscription.debounceTimer = setTimeout(() => {
          this.$log.debug(`[WssSubscription][${componentName}] Debounce complete, executing handler`);
          this._executeHandler(config.handler, event, context);
          if (config.once) {
            unsubscribe();
          }
        }, config.debounce);
      } else {
        this.$log.debug(`[WssSubscription][${componentName}] Executing handler immediately`);
        this._executeHandler(config.handler, event, context);
        if (config.once) {
          unsubscribe();
        }
      }
    };

    activeSubscription.handler = wrappedHandler as WssEventHandler;

    // Subscribe
    this.$log.debug(`[WssSubscription][${componentName}] Subscribing to event:`, config.event);
    if (config.once && !config.debounce) {
      WssEventEmitter.once(config.event, wrappedHandler as WssEventHandler);
    } else {
      WssEventEmitter.on(config.event, wrappedHandler as WssEventHandler);
    }
    this.$log.debug(`[WssSubscription][${componentName}] Subscribed successfully to:`, config.event);

    this._wssActiveSubscriptions.push(activeSubscription);

    return unsubscribe;
  }

  /**
   * Execute a handler with error handling
   */
  private async _executeHandler<T>(
    handler: IWssSubscriptionConfig<T>["handler"],
    event: IWssEvent<T>,
    context: IWssSubscriptionContext
  ): Promise<void> {
    try {
      await handler(event, context);
    } catch (error) {
      console.warn("[WssSubscriptionMixin] Error in subscription handler:", error);
    }
  }
}
